import { LoadingButton } from "@/components/ui/loading-button/LoadingButton";
import { Separator } from "@/components/ui/Separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet";
import { useToast } from "@/components/ui/UseToast";
import React, { useEffect } from "react";
import { FloatingLabelInput } from "@/components/ui/floating-lable/Floating";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/ui/Button";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import { Event, EventStatus } from "@/app/types/event";
import { useCreateEventMutation, useUpdateEventMutation } from "@/services/EventApi";
import { Badge } from "@/components/ui/Badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { Calendar } from "@/components/ui/Calendar";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import moment from "moment";

interface EventFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  action: "add" | "edit";
  setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
  refetch: () => void;
  setData: React.Dispatch<React.SetStateAction<Event | undefined>>;
  data: Event | undefined;
}

const EventForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  refetch,
  data,
  setData,
}: EventFormProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.org_id) ?? 0;

  const initialValues: Partial<Event> = {
    name: "",
    description: "",
    status: EventStatus.INACTIVE,
    org_id: orgId,
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().add(1, "day").format("YYYY-MM-DD"),
  };

  const [createEvent] = useCreateEventMutation();
  const [updateEvent] = useUpdateEventMutation();
  const { toast } = useToast();

  const form = useForm<Event>({
    mode: "all",
    defaultValues: initialValues as Event,
  });

  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = form;

  useEffect(() => {
    if (action === "edit" && data) {
      reset({
        ...data,
        start_date: moment.utc(data.start_date).format("YYYY-MM-DD"),
        end_date: moment.utc(data.end_date).format("YYYY-MM-DD"),
      });
    } else if (action === "add") {
      reset(initialValues as Event);
    }
  }, [action, data, reset]);

  const handleClose = () => {
    clearErrors();
    reset();
    setAction("add");
    setData(undefined);
    setOpen(false);
  };

  const onSubmit = async (payload: Event) => {
    try {
      // Ensure ISO format for backend
        const body = {
          ...payload,
          // Keep dates as YYYY-MM-DD strings to avoid timezone offset issues
          start_date: payload.start_date,
          end_date: payload.end_date,
          org_id: orgId,
        };

      if (action === "add") {
        await createEvent(body).unwrap();
      } else {
        await updateEvent({ event_id: payload.event_id, body }).unwrap();
      }
      
      toast({
        variant: "success",
        title: action === "add" ? "Event created successfully" : "Event updated successfully",
      });

      refetch();
      handleClose();
    } catch (error: any) {
      const errorMessage = error?.data?.message || "Something went wrong";
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  return (
    <Sheet open={isOpen}>
      <SheetContent
        hideCloseButton
        className="!max-w-[800px] p-0 custom-scrollbar sm:w-[90%] sm:max-w-xl"
      >
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className="flex flex-col gap-4 bg-gray-100 h-screen"
            noValidate
          >
            <SheetHeader className="sticky top-0 z-40 px-4 py-3 bg-white border-b-gray-200 border-b">
              <SheetTitle>
                <div className="flex justify-between items-center">
                  <p className="font-semibold">
                    {action === "add" ? "Create" : "Edit"} Event
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setOpen(false)}
                      className="w-[100px]"
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      type="submit"
                      loading={isSubmitting}
                      className="w-[120px]"
                    >
                      Save
                    </LoadingButton>
                  </div>
                </div>
              </SheetTitle>
            </SheetHeader>
            <SheetDescription className="p-4 mx-4 mt-4 bg-white rounded-lg overflow-y-auto">
              <h2 className="font-medium">Event Details</h2>
              <Separator className="my-2" />
              
              <div className="grid grid-cols-1 gap-6 mt-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Event Name <span className="text-red-500">*</span></label>
                  <FloatingLabelInput
                    placeholder="Enter Event Name"
                    {...register("name", { required: "Required" })}
                    error={errors.name?.message}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Description</label>
                  <FloatingLabelInput
                    placeholder="Enter Description"
                    type="textarea"
                    rows={3}
                    {...register("description")}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">Start Date <span className="text-red-500">*</span></label>
                    <Controller
                      name="start_date"
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field: { onChange, value } }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal border-grey text-grey",
                                !value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-grey" />
                              {value ? moment(value).format("MMM DD, YYYY") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={value ? moment(value).toDate() : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  onChange(moment(date).format("YYYY-MM-DD"));
                                }
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                    {errors.start_date && <span className="text-red-500 text-xs">{errors.start_date.message}</span>}
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium">End Date <span className="text-red-500">*</span></label>
                    <Controller
                      name="end_date"
                      control={control}
                      rules={{ required: "Required" }}
                      render={({ field: { onChange, value } }) => (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal border-grey text-grey",
                                !value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-grey" />
                              {value ? moment(value).format("MMM DD, YYYY") : <span>Pick a date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={value ? moment(value).toDate() : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  onChange(moment(date).format("YYYY-MM-DD"));
                                }
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    />
                    {errors.end_date && <span className="text-red-500 text-xs">{errors.end_date.message}</span>}
                  </div>
                </div>



                <div className="flex items-center justify-between p-3 border rounded-md">
                   <div className="flex flex-col">
                      <label className="text-sm font-medium">Initial Status</label>
                      <span className="text-xs text-gray-500">Global status of the event</span>
                   </div>
                    <Controller
                    name="status"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex items-center gap-2">
                        <Badge className={
                          value === EventStatus.ACTIVE ? "bg-green-600" : 
                          value === EventStatus.ARCHIVE ? "bg-yellow-500" :
                          "bg-red-500"
                        } style={{ textTransform: 'capitalize' }}>
                          {value}
                        </Badge>
                        <Select onValueChange={onChange} value={value}>
                          <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={EventStatus.ARCHIVE}>Archive</SelectItem>
                            <SelectItem value={EventStatus.ACTIVE}>Active</SelectItem>
                            <SelectItem value={EventStatus.INACTIVE}>Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  />
                </div>
              </div>
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          setIsConfirmOpen(false);
          handleClose();
        }}
      />
    </Sheet>
  );
};

export default EventForm;
